<template>
  <div class="staff-manage">
    <el-card ref="searchCard" shadow="never">
      <div class="search-area">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="姓名">
            <el-input v-model="searchForm.staffName" placeholder="请输入用户名或真实姓名" clearable />
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="searchForm.phoneNumber" placeholder="请输入手机号" clearable />
          </el-form-item>
          <el-form-item label="会员等级">
            <el-select v-model="searchForm.memberLevelCode" placeholder="请选择" clearable>
              <el-option label="非会员" value="NON_MEMBER" />
              <el-option label="认证会员" value="AUTH_MEMBER" />
              <el-option label="高级会员" value="ADVANCED_MEMBER" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="searchForm.statusCode" placeholder="请选择" clearable>
              <el-option label="正常" value="ACTIVE" />
              <el-option label="禁用" value="DISABLED" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch" :loading="searchLoading">查询</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="btn-group">
          <el-button v-if="canShowManageListDelete" icon="el-icon-delete" :disabled="!selectedRows.length" @click="handleBatchDelete" :loading="batchDeleteLoading">批量删除</el-button>
          <el-button v-if="canShowAddMember" icon="el-icon-plus" @click="handleAdd">新增会员</el-button>
        </div>
      </div>
    </el-card>

    <el-card ref="tableCard" shadow="never" class="table-card">
      <el-table ref="staffListTable" :height="staffTableHeight" :data="tableData" :align="left" v-loading="loading" border stripe @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="50" :selectable="checkSelectable" />
        <el-table-column type="index" label="序号" width="60" :index="indexMethod" />
        <el-table-column prop="username" label="用户名" min-width="120" />
        <el-table-column prop="staffName" label="真实姓名" min-width="100" />
        <el-table-column prop="phoneNumber" label="手机号" width="130">
          <template slot-scope="{ row }">
            {{ maskPhoneForTable(row.phoneNumber) }}
          </template>
        </el-table-column>
        <el-table-column prop="wechatNumber" label="微信号" width="130" />
        <el-table-column prop="parentName" label="上级会员" width="120">
          <template slot-scope="{ row }">
            {{ row.parentName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="memberLevelName" label="会员等级" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="getMemberLevelType(row.memberLevelCode)">{{ row.memberLevelName || row.memberLevelCode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="referrerDealRate" align="right" width="118">
          <template slot="header">
            <span class="staff-deal-rate-header">
              <span>成交率</span>
              <el-popover placement="top" trigger="hover" width="240">
                <div class="staff-deal-rate-popover-text">成交率 = 订单数 / 推荐学员数</div>
                <i
                  slot="reference"
                  class="el-icon-info staff-deal-rate-info-icon"
                  tabindex="0"
                  role="button"
                  aria-label="成交率说明"
                />
              </el-popover>
            </span>
          </template>
          <template slot-scope="{ row }">
            {{ formatReferrerDealRate(row.referrerDealRate) }}
          </template>
        </el-table-column>
        <el-table-column prop="statusName" label="状态" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="row.statusCode === 'ACTIVE' ? 'success' : 'danger'">{{ row.statusName || row.statusCode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="300" fixed="right">
          <template slot-scope="{ row }">
            <div class="op-row">
              <el-button type="text" @click="handleDetail(row)">详情</el-button>
              <el-button v-if="canShowStaffRowEdit" type="text" @click="handleEdit(row)">编辑</el-button>
              <el-button
                v-if="canShowManageListDelete && row.username !== 'systemManager'"
                type="text"
                class="danger-btn"
                @click="handleDelete(row)"
              >
                删除
              </el-button>
            </div>
            <div class="op-row">
              <el-button type="text" @click="handleOrders(row)">订单</el-button>
              <el-button type="text" @click="handleTeam(row)">队伍</el-button>
              <el-popover
                popper-class="staff-account-popper"
                placement="top"
                trigger="click"
                width="300"
                @show="onAccountPopoverShow(row)"
              >
                <div class="account-popover">
                  <div class="account-popover__line">
                    可提现余额：{{ formatMoney2(accountSummaryMap[row.staffId]?.withdrawableBalance) }}
                  </div>
                  <div class="account-popover__line">
                    已提现金额：{{ formatMoney2(accountSummaryMap[row.staffId]?.withdrawnAmount) }}
                  </div>
                  <div class="account-popover__line">
                    待审核金额：{{ formatMoney2(accountSummaryMap[row.staffId]?.pendingAuditAmount) }}
                  </div>
                  <div v-if="accountLoadingMap[row.staffId]" class="account-popover__loading">加载中...</div>
                </div>
                <el-button slot="reference" type="text" class="op-btn-account">账户</el-button>
              </el-popover>
              <el-button type="text" class="op-btn-withdraw-record" @click="handleStaffWithdrawRecords(row)">提现记录</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <div ref="paginationWrapper" class="pagination-wrapper">
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="960px" :close-on-click-modal="false" @close="resetForm" @open="onDialogOpen">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="用户名" prop="username">
              <el-input v-model="form.username" :title="detailTooltip(form.username)" :disabled="isDetailReadOnly" placeholder="请输入用户名" maxlength="100" show-word-limit style="width: 350px;" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="真实姓名" prop="staffName">
              <el-input v-model="form.staffName" :title="detailTooltip(form.staffName)" :disabled="isDetailReadOnly" placeholder="请输入真实姓名" maxlength="100" show-word-limit style="width: 350px;" clearable />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="!isDetailReadOnly" :gutter="20">
          <el-col :span="12">
            <el-form-item label="密码" prop="password" :required="!isEdit">
              <el-input v-model="form.password" type="password" :placeholder="isEdit ? '不修改请留空' : '请输入密码'" maxlength="50" show-password style="width: 350px;" clearable />
            </el-form-item>
            <div class="password-strength">
              <span class="strength-label">密码强度：</span>
              <el-rate
                :value="passwordStrength"
                disabled
                :max="5"
                show-text
                :texts="['弱', '一般', '中等', '较强', '非常强']"
                :colors="['#00FF00', '#00FF00', '#00FF00']"
              />
            </div>
          </el-col>
          <el-col :span="12">
            <el-form-item label="确认密码" prop="confirmPassword" :required="!isEdit">
              <el-input v-model="form.confirmPassword" type="password" :placeholder="isEdit ? '不修改请留空' : '请再次输入密码'" maxlength="50" show-password style="width: 350px;" clearable />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="手机号" prop="phoneNumber">
              <el-input v-model="form.phoneNumber" :title="detailTooltip(form.phoneNumber)" :disabled="isDetailReadOnly" placeholder="请输入手机号" style="width: 350px;" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="微信号">
              <el-input v-model="form.wechatNumber" :title="detailTooltip(form.wechatNumber)" :disabled="isDetailReadOnly" placeholder="请输入微信号" style="width: 350px;" clearable />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开户行" prop="openingBank">
              <el-input
                v-model="form.openingBank"
                :title="detailTooltip(form.openingBank)"
                :disabled="isDetailReadOnly"
                placeholder="请输入开户行"
                maxlength="150"
                show-word-limit
                style="width: 350px;"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="银行卡号" prop="bankCardNo">
              <el-input
                v-model="form.bankCardNo"
                :title="detailTooltip(form.bankCardNo)"
                :disabled="isDetailReadOnly"
                placeholder="请输入银行卡号（8-50位数字）"
                maxlength="50"
                show-word-limit
                style="width: 350px;"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="会员等级" prop="memberLevelCode">
              <el-select v-model="form.memberLevelCode" :title="detailTooltip(detailMemberLevelLabel)" :disabled="isDetailReadOnly" placeholder="请选择" style="width: 350px;">
                <el-option label="非会员" value="NON_MEMBER" />
                <el-option label="认证会员" value="AUTH_MEMBER" />
                <el-option label="高级会员" value="ADVANCED_MEMBER" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="!isDetailReadOnly && form.memberLevelCode && form.memberLevelCode !== 'NON_MEMBER'">
            <el-form-item label="上级会员" prop="parentId" :required="form.memberLevelCode === 'AUTH_MEMBER'">
              <el-select 
                v-model="form.parentId" 
                placeholder="请选择上级会员" 
                style="width: 350px;" 
                filterable 
                :loading="staffLoading"
                @visible-change="onSelectVisibleChange"
              >
                <el-option
                  v-for="item in staffOptions"
                  :key="item.staffId"
                  :label="`${item.username || item.staffName || '-'}（${item.phoneNumber || ''}）`"
                  :value="item.staffId"
                />
                <div class="select-pagination">
                  <el-button type="text" :disabled="staffPage <= 1" @click.stop="prevStaffPage">
                    <i class="el-icon-arrow-left"></i> 上一页
                  </el-button>
                  <span class="page-info">{{ staffPage }} / {{ staffTotalPages }}</span>
                  <el-button type="text" :disabled="staffPage >= staffTotalPages" @click.stop="nextStaffPage">
                    下一页 <i class="el-icon-arrow-right"></i>
                  </el-button>
                </div>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="isDetailReadOnly && form.memberLevelCode && form.memberLevelCode !== 'NON_MEMBER'">
            <el-form-item label="上级会员">
              <el-input :value="detailParentName || '-'" :title="detailTooltip(detailParentName)" disabled style="width: 350px;" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="账号状态" prop="statusCode">
              <el-select v-model="form.statusCode" :title="detailTooltip(detailStatusLabel)" :disabled="isDetailReadOnly" placeholder="请选择" style="width: 350px;">
                <el-option label="正常" value="ACTIVE" />
                <el-option label="禁用" value="DISABLED" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="isDetailReadOnly && detailCreateTime" :span="12">
            <el-form-item label="创建时间">
              <el-input :value="detailCreateTime" :title="detailTooltip(detailCreateTime)" disabled style="width: 350px;" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">{{ isDetailReadOnly ? '关闭' : '取消' }}</el-button>
        <el-button v-if="!isDetailReadOnly" type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="teamDialogTitle" :visible.sync="teamDialogVisible" width="760px">
      <el-table :data="teamTableData" v-loading="teamLoading" border stripe>
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="username" label="用户名" min-width="110" show-overflow-tooltip />
        <el-table-column prop="staffName" label="真实姓名" min-width="100" show-overflow-tooltip />
        <el-table-column prop="phoneNumber" label="手机号" width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ maskPhoneForTable(row.phoneNumber) }}
          </template>
        </el-table-column>
        <el-table-column prop="memberLevelName" label="会员等级" width="120">
          <template slot-scope="{ row }">
            <el-tag :type="getMemberLevelType(row.memberLevelCode)">{{ row.memberLevelName || row.memberLevelCode || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="statusName" label="会员状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.statusCode === 'ACTIVE' ? 'success' : 'danger'">{{ row.statusName || row.statusCode || '-' }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog
      :title="withdrawDialogTitle"
      :visible.sync="withdrawDialogVisible"
      width="920px"
      :close-on-click-modal="true"
    >
      <div class="withdraw-records-wrap" v-loading="withdrawLoading">
        <el-timeline v-if="withdrawRecords.length">
          <el-timeline-item
            v-for="item in withdrawRecords"
            :key="item.id"
            :timestamp="item.createdAt || '-'"
            placement="top"
          >
            <el-card shadow="hover" class="withdraw-record-card">
              <div class="withdraw-record-card__top">
                <div class="withdraw-record-card__amt">¥{{ item.totalAmount || '0.00' }}</div>
                <div :class="statusTextClass(item.status)">
                  {{ withdrawStatusLabel(item.status) }}
                </div>
              </div>
              <div class="withdraw-record-card__meta">
                <span>账单数：{{ Number(item.billCount || 0) }}</span>
                <span class="withdraw-record-card__sep">·</span>
                <span>审核时间：{{ item.reviewedAt || '-' }}</span>
                <span class="withdraw-record-card__sep">·</span>
                <span>
                  是否打款：<span :class="paidTextClass(item.paidStatus)">{{ paidStatusLabel(item.paidStatus) }}</span>
                </span>
                <span class="withdraw-record-card__sep">·</span>
                <span>打款日期：{{ item.paidAt || '-' }}</span>
              </div>
              <div class="withdraw-record-card__meta">
                <span>持卡人姓名：{{ item.bankCardHolder || '-' }}</span>
                <span class="withdraw-record-card__sep">·</span>
                <span>银行卡号：{{ item.bankCardNoPlain || item.bankCardNoMask || '-' }}</span>
                <span class="withdraw-record-card__sep">·</span>
                <span>开户银行：{{ item.bankName || '-' }}</span>
              </div>
              <div v-if="item.rejectReason" class="withdraw-record-card__reason">
                <div class="withdraw-record-card__reason-label">驳回原因</div>
                <div class="withdraw-record-card__reason-body">{{ item.rejectReason }}</div>
              </div>
            </el-card>
          </el-timeline-item>
        </el-timeline>
        <div v-else class="withdraw-records-empty">暂无提现记录</div>
      </div>
      <span slot="footer">
        <el-button @click="withdrawDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../services/api';
import { canShowOrgStaffStudentListDelete, getRoleCode, getUserRole } from '@/utils/sessionAuth';

export default {
  name: 'StaffManage',
  data() {
    return {
      loading: false,
      submitLoading: false,
      staffLoading: false,
      searchLoading: false,
      deleteLoading: false,
      batchDeleteLoading: false,
      tableData: [],
      teamDialogVisible: false,
      teamDialogTitle: '队伍',
      teamLoading: false,
      teamTableData: [],
      withdrawDialogVisible: false,
      withdrawDialogTitle: '提现记录',
      withdrawLoading: false,
      withdrawRecords: [],
      accountSummaryMap: {},
      accountLoadingMap: {},
      selectedRows: [],
      staffOptions: [],
      staffPage: 1,
      staffPageSize: 10,
      staffTotal: 0,
      total: 0,
      currentPage: 1,
      pageSize: 10,
      staffTableHeight: 420,
      searchForm: {
        staffName: '',
        phoneNumber: '',
        memberLevelCode: '',
        statusCode: ''
      },
      dialogVisible: false,
      dialogTitle: '新增会员',
      isEdit: false,
      isDetailReadOnly: false,
      editId: null,
      detailParentName: '',
      detailCreateTime: '',
      form: {
        staffName: '',
        phoneNumber: '',
        username: '',
        password: '',
        confirmPassword: '',
        wechatNumber: '',
        parentId: '',
        roleId: '',
        memberLevelCode: 'NON_MEMBER',
        statusCode: 'ACTIVE'
      },
      rules: {
        staffName: [{ max: 100, message: '真实姓名最多100个字符', trigger: ['blur', 'change'] }],
        phoneNumber: [
          { required: true, message: '请输入手机号', trigger: ['change', 'blur'] },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的11位手机号', trigger: ['change', 'blur'] }
        ],
        username: [
          { required: true, message: '请输入用户名', trigger: ['change', 'blur'] },
          { max: 100, message: '用户名最多100个字符', trigger: ['change', 'blur'] },
          { pattern: /^[\u4e00-\u9fa5a-zA-Z0-9\-·_]+$/, message: '只允许中英文、数字、连接符(-)、·和下划线(_)', trigger: ['change', 'blur'] }
        ],
        password: [
          { 
            validator: (rule, value, callback) => {
              if (this.isDetailReadOnly) return callback();
              // 新增时必填，编辑时可选
              if (!this.isEdit && !value) {
                return callback(new Error('请输入密码'));
              }
              if (!value) return callback();
              if (value.length < 6 || value.length > 50) {
                return callback(new Error('密码长度6-50位'));
              }
              const hasLetter = /[a-zA-Z]/.test(value);
              const hasNumber = /\d/.test(value);
              const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(value);
              const typeCount = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
              if (typeCount < 2) {
                callback(new Error('密码至少由字母、数字、特殊符号中的两种组成'));
              } else {
                if (this.form.confirmPassword) {
                  this.$refs.form.validateField('confirmPassword');
                }
                callback();
              }
            }, 
            trigger: ['change', 'blur'] 
          }
        ],
        confirmPassword: [
          { 
            validator: (rule, value, callback) => {
              if (this.isDetailReadOnly) return callback();
              // 新增时必填，编辑时如果填了密码则必须确认
              if (!this.isEdit && !value) {
                return callback(new Error('请确认密码'));
              }
              if (this.form.password && !value) {
                return callback(new Error('请确认密码'));
              }
              if (!value) return callback();
              if (value !== this.form.password) {
                callback(new Error('两次输入的密码不一致'));
              } else {
                callback();
              }
            }, 
            trigger: ['change', 'blur'] 
          }
        ],
        parentId: [
          {
            validator: (rule, value, callback) => {
              if (this.isDetailReadOnly) return callback();
              if (!this.form.memberLevelCode || this.form.memberLevelCode === 'NON_MEMBER') return callback();
              if (this.form.memberLevelCode === 'AUTH_MEMBER' && !value) {
                return callback(new Error('请选择上级会员'));
              }
              callback();
            },
            trigger: ['blur', 'change']
          }
        ],
        memberLevelCode: [{ required: true, message: '请选择会员等级', trigger: ['blur', 'change'] }],
        statusCode: [{ required: true, message: '请选择账号状态', trigger: ['blur', 'change'] }],
        openingBank: [
          { max: 150, message: '开户行最多150个字', trigger: ['blur', 'change'] }
        ],
        bankCardNo: [
          { max: 50, message: '银行卡号最多50个字符', trigger: ['blur', 'change'] },
          {
            validator: (rule, value, callback) => {
              if (this.isDetailReadOnly) return callback();
              if (value == null || String(value).trim() === '') return callback();
              const digits = String(value).replace(/\s/g, '');
              if (!/^\d{8,50}$/.test(digits)) {
                return callback(new Error('银行卡号须为8-50位数字（仅数字，空格不计入位数）'));
              }
              callback();
            },
            trigger: ['blur', 'change']
          }
        ]
      }
    };
  },
  computed: {
    /** 核算人员（FINANCE）、运营人员（OPERATION）不可新增会员 */
    canShowAddMember() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      return rc !== 'FINANCE' && ur !== 'FINANCE' && rc !== 'OPERATION' && ur !== 'OPERATION';
    },
    /** 运营人员（OPERATION）不可编辑会员 */
    canShowStaffRowEdit() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      return rc !== 'OPERATION' && ur !== 'OPERATION';
    },
    canShowManageListDelete() {
      return canShowOrgStaffStudentListDelete();
    },
    staffTotalPages() {
      return Math.ceil(this.staffTotal / this.staffPageSize) || 1;
    },
    /** 详情弹窗：会员等级展示文案（用于 title） */
    detailMemberLevelLabel() {
      const m = { NON_MEMBER: '非会员', AUTH_MEMBER: '认证会员', ADVANCED_MEMBER: '高级会员' };
      const c = this.form.memberLevelCode;
      return m[c] || c || '';
    },
    /** 详情弹窗：账号状态展示文案（用于 title） */
    detailStatusLabel() {
      const m = { ACTIVE: '正常', DISABLED: '禁用' };
      const c = this.form.statusCode;
      return m[c] || c || '';
    },
    passwordStrength() {
      const pwd = this.form.password || '';
      if (!pwd) return 0;
      
      let score = 0;
      // 长度评分
      if (pwd.length >= 6) score++;
      if (pwd.length >= 8) score++;
      if (pwd.length >= 12) score++;
      // 复杂度评分
      if (/[a-z]/.test(pwd)) score++;
      if (/[A-Z]/.test(pwd)) score++;
      if (/\d/.test(pwd)) score++;
      if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pwd)) score++;
      
      // 映射到1-5等级
      if (score <= 2) return 1;      // 弱
      if (score <= 3) return 2;      // 一般
      if (score <= 4) return 3;      // 中等
      if (score <= 5) return 4;      // 较强
      return 5;                       // 非常强
    }
  },
  watch: {
    'form.memberLevelCode'(val) {
      if (this.isDetailReadOnly) return;
      if (val === 'NON_MEMBER') {
        this.form.parentId = '';
        this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate('parentId'));
      }
    }
  },
  mounted() {
    this.loadData();
    this.$nextTick(() => {
      this.updateStaffTableHeight();
    });
    window.addEventListener('resize', this.updateStaffTableHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateStaffTableHeight);
  },
  methods: {
    updateStaffTableHeight() {
      const tableCardRef = this.$refs.tableCard;
      const tableCardEl = tableCardRef && tableCardRef.$el ? tableCardRef.$el : null;
      if (!tableCardEl) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
      const tableTop = tableCardEl.getBoundingClientRect().top;
      const paginationEl = this.$refs.paginationWrapper;
      const paginationHeight = paginationEl ? paginationEl.getBoundingClientRect().height : 56;
      const safeBottomGap = 12;
      const computed = Math.floor(viewportHeight - tableTop - paginationHeight - safeBottomGap);
      this.staffTableHeight = Math.max(260, computed);

      this.$nextTick(() => {
        const tableRef = this.$refs.staffListTable;
        if (tableRef && typeof tableRef.doLayout === 'function') {
          tableRef.doLayout();
        }
      });
    },
    /** 仅详情弹窗：控件原生 title，悬停显示完整内容（禁用时易截断） */
    detailTooltip(value) {
      if (!this.isDetailReadOnly) return undefined;
      if (value === undefined || value === null) return undefined;
      const s = String(value);
      return s === '' ? undefined : s;
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    maskPhoneForTable(phone) {
      const text = String(phone || '').trim();
      if (!text) return '-';
      if (/^1[3-9]\d{9}$/.test(text)) {
        return text.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
      }
      return text;
    },
    checkSelectable(row) {
      if (!this.canShowManageListDelete) return false;
      return row.username !== 'systemManager';
    },
    getMemberLevelType(code) {
      const map = { NON_MEMBER: 'info', AUTH_MEMBER: '', ADVANCED_MEMBER: 'warning' };
      return map[code] || 'info';
    },
    /** 推荐成交率：订单数/学员数×100，列表展示为百分比 */
    formatReferrerDealRate(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '0.00%';
      return `${n.toFixed(2)}%`;
    },
    formatMoney2(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '0.00';
      return n.toFixed(2);
    },
    async onAccountPopoverShow(row) {
      const staffId = row && row.staffId ? String(row.staffId).trim() : '';
      if (!staffId) return;
      if (this.accountLoadingMap[staffId]) return;
      if (this.accountSummaryMap[staffId]) return;
      this.$set(this.accountLoadingMap, staffId, true);
      try {
        const res = await api.post('/staff/parttime-account/manage-summary', { staffId });
        const d = (res && res.data && res.data.data) || {};
        this.$set(this.accountSummaryMap, staffId, {
          withdrawableBalance: d.withdrawableBalance || '0.00',
          withdrawnAmount: d.withdrawnAmount || '0.00',
          pendingAuditAmount: d.pendingAuditAmount || '0.00'
        });
      } catch (e) {
        this.$set(this.accountSummaryMap, staffId, {
          withdrawableBalance: '0.00',
          withdrawnAmount: '0.00',
          pendingAuditAmount: '0.00'
        });
      } finally {
        this.$set(this.accountLoadingMap, staffId, false);
      }
    },
    handleOrders(row) {
      this.$router.push({ name: 'StaffOrders', query: { staffId: row.staffId } });
    },
    async handleTeam(row) {
      this.teamDialogVisible = true;
      this.teamDialogTitle = `${row.staffName || row.username || ''} 的队伍`;
      this.teamLoading = true;
      this.teamTableData = [];
      try {
        const res = await api.post(`/staff/${row.staffId}/children`, {});
        if (res.data && res.data.success) {
          this.teamTableData = res.data.data || [];
        }
      } catch (e) {
        this.$message.error('加载队伍数据失败');
      }
      this.teamLoading = false;
    },
    /** 提现记录（占位，后续接页面或弹窗） */
    normalizeWithdrawStatus(s) {
      return String(s || '').trim().toUpperCase();
    },
    normalizePaidStatus(s) {
      return String(s || '').trim().toUpperCase();
    },
    withdrawStatusLabel(s) {
      const u = this.normalizeWithdrawStatus(s);
      if (u === 'PENDING') return '待审核';
      if (u === 'APPROVED') return '已通过';
      if (u === 'REJECTED') return '已驳回';
      return s || '-';
    },
    paidStatusLabel(s) {
      const u = this.normalizePaidStatus(s);
      if (u === 'PAID') return '已打款';
      return '未打款';
    },
    paidTextClass(s) {
      const u = this.normalizePaidStatus(s);
      if (u === 'PAID') return 'paid-text paid-text--paid';
      return 'paid-text paid-text--unpaid';
    },
    statusTextClass(s) {
      const u = this.normalizeWithdrawStatus(s);
      if (u === 'APPROVED') return 'withdraw-status withdraw-status--approved';
      if (u === 'REJECTED') return 'withdraw-status withdraw-status--rejected';
      if (u === 'PENDING') return 'withdraw-status withdraw-status--pending';
      return 'withdraw-status';
    },
    async handleStaffWithdrawRecords(row) {
      this.withdrawDialogVisible = true;
      const name = row && (row.staffName || row.username || row.staffId) ? (row.staffName || row.username || row.staffId) : '';
      this.withdrawDialogTitle = `${name} 的提现记录`;
      this.withdrawLoading = true;
      this.withdrawRecords = [];
      try {
        const res = await api.post('/staff/withdrawal/manage/staff-records', { staffId: row.staffId });
        if (res.data && res.data.success) {
          this.withdrawRecords = Array.isArray(res.data.data) ? res.data.data : [];
        } else {
          this.withdrawRecords = [];
        }
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message ? e.response.data.message : '加载提现记录失败';
        this.$message.error(msg);
        this.withdrawRecords = [];
      }
      this.withdrawLoading = false;
    },
    async loadStaffOptions() {
      this.staffLoading = true;
      try {
        const res = await api.post('/staff/list', {
          memberLevelCode: 'ADVANCED_MEMBER',
          page: this.staffPage,
          pageSize: this.staffPageSize
        });
        if (res.data.success) {
          this.staffOptions = res.data.data.list || [];
          this.staffTotal = res.data.data.total || 0;
        }
      } catch (e) {
        console.error('加载会员列表失败', e);
      }
      this.staffLoading = false;
    },
    prevStaffPage() {
      if (this.staffPage > 1) {
        this.staffPage--;
        this.loadStaffOptions();
      }
    },
    nextStaffPage() {
      if (this.staffPage < this.staffTotalPages) {
        this.staffPage++;
        this.loadStaffOptions();
      }
    },
    onSelectVisibleChange(visible) {
      if (visible) {
        this.staffPage = 1;
        this.loadStaffOptions();
      }
    },
    onDialogOpen() {
      if (this.isDetailReadOnly) return;
      this.staffPage = 1;
      this.loadStaffOptions();
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await api.post('/staff/list', {
          ...this.searchForm,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        if (res.data.success) {
          this.tableData = res.data.data.list || [];
          this.total = res.data.data.total || 0;
        }
      } catch (e) {
        this.$message.error('加载数据失败');
      }
      this.loading = false;
      this.$nextTick(() => {
        this.updateStaffTableHeight();
      });
    },
    async handleSearch() {
      this.searchLoading = true;
      this.currentPage = 1;
      await this.loadData();
      this.searchLoading = false;
    },
    handleReset() {
      this.searchForm = { staffName: '', phoneNumber: '', memberLevelCode: '', statusCode: '' };
      this.handleSearch();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.loadData();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadData();
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    handleAdd() {
      if (!this.canShowAddMember) return;
      this.dialogTitle = '新增会员';
      this.isEdit = false;
      this.isDetailReadOnly = false;
      this.detailParentName = '';
      this.detailCreateTime = '';
      this.editId = null;
      this.dialogVisible = true;
    },
    async handleDetail(row) {
      try {
        const res = await api.post(`/staff/${row.staffId}`, {});
        if (!res.data.success || !res.data.data) {
          this.$message.error(res.data.message || '获取会员详情失败');
          return;
        }
        const d = res.data.data;
        this.isDetailReadOnly = true;
        this.dialogTitle = '会员详情';
        this.isEdit = true;
        this.editId = d.staffId;
        const parentName = d.parentUsername || d.parentStaffName || '';
        const parentPhone = d.parentPhoneNumber || '';
        this.detailParentName = d.parentId
          ? `${parentName || '-'}（${parentPhone}）`
          : '-';
        this.detailCreateTime = d.createTime || '';
        this.form = {
          staffName: d.staffName,
          phoneNumber: d.phoneNumber,
          username: d.username || '',
          password: '',
          confirmPassword: '',
          wechatNumber: d.wechatNumber || '',
          parentId: d.parentId || '',
          roleId: d.roleId || '',
          memberLevelCode: d.memberLevelCode,
          statusCode: d.statusCode,
          openingBank: d.openingBank || '',
          bankCardNo: d.bankCardNo || ''
        };
        this.dialogVisible = true;
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate();
        });
      } catch (e) {
        this.$message.error(e.response?.data?.message || '获取会员详情失败');
      }
    },
    handleEdit(row) {
      if (!this.canShowStaffRowEdit) return;
      this.dialogTitle = '编辑会员';
      this.isEdit = true;
      this.isDetailReadOnly = false;
      this.detailParentName = '';
      this.detailCreateTime = '';
      this.editId = row.staffId;
      this.form = {
        staffName: row.staffName,
        phoneNumber: row.phoneNumber,
        username: row.username || '',
        password: '',
        confirmPassword: '',
        wechatNumber: row.wechatNumber || '',
        parentId: row.parentId || '',
        roleId: row.roleId || '',
        memberLevelCode: row.memberLevelCode,
        statusCode: row.statusCode,
        openingBank: row.openingBank || '',
        bankCardNo: row.bankCardNo || ''
      };
      this.dialogVisible = true;
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定删除该会员吗？', '提示', { type: 'warning' });
        this.deleteLoading = true;
        const res = await api.post(`/staff/${row.staffId}/remove`);
        if (res.data.success) {
          this.$message.success('删除成功');
          this.loadData();
        } else {
          this.$message.error(res.data.message || '删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败');
      } finally {
        this.deleteLoading = false;
      }
    },
    async handleBatchDelete() {
      if (!this.selectedRows.length) return;
      try {
        await this.$confirm(`确定删除选中的 ${this.selectedRows.length} 条记录吗？`, '提示', { type: 'warning' });
        this.batchDeleteLoading = true;
        let successCount = 0;
        for (const row of this.selectedRows) {
          try {
            const res = await api.post(`/staff/${row.staffId}/remove`);
            if (res.data.success) successCount++;
          } catch (e) {
            console.error('删除失败', e);
          }
        }
        this.$message.success(`成功删除 ${successCount} 条记录`);
        this.loadData();
      } catch (e) {
        if (e !== 'cancel') this.$message.error('批量删除失败');
      } finally {
        this.batchDeleteLoading = false;
      }
    },
    async handleSubmit() {
      if (this.isDetailReadOnly) return;
      try {
        await this.$refs.form.validate();
      } catch (e) {
        this.$nextTick(() => {
          const errorEl = document.querySelector('.el-form-item.is-error');
          if (errorEl) {
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      this.submitLoading = true;
      try {
        const submitData = {
          staffName: this.form.staffName,
          username: this.form.username,
          phoneNumber: this.form.phoneNumber,
          wechatNumber: this.form.wechatNumber,
          parentId: this.form.memberLevelCode === 'NON_MEMBER' ? '' : this.form.parentId,
          roleId: this.form.roleId,
          memberLevelCode: this.form.memberLevelCode,
          statusCode: this.form.statusCode,
          openingBank: this.form.openingBank,
          bankCardNo: this.form.bankCardNo != null && String(this.form.bankCardNo).trim() !== ''
            ? String(this.form.bankCardNo).replace(/\s/g, '')
            : ''
        };
        if (this.form.password) {
          submitData.password = this.form.password;
        }
        let res;
        if (this.isEdit) {
          res = await api.post(`/staff/${this.editId}/update`, submitData);
        } else {
          res = await api.post('/staff/add', submitData);
        }
        if (res.data.success) {
          this.$message.success(this.isEdit ? '更新成功' : '新增成功');
          this.dialogVisible = false;
          this.loadData();
        } else {
          this.$message.error(res.data.message || '操作失败');
        }
      } catch (e) {
        this.$message.error(e.response?.data?.message || '操作失败');
      }
      this.submitLoading = false;
    },
    resetForm() {
      this.isDetailReadOnly = false;
      this.detailParentName = '';
      this.detailCreateTime = '';
      this.form = {
        staffName: '',
        phoneNumber: '',
        username: '',
        password: '',
        confirmPassword: '',
        wechatNumber: '',
        parentId: '',
        roleId: '',
        memberLevelCode: 'NON_MEMBER',
        statusCode: 'ACTIVE',
        openingBank: '',
        bankCardNo: ''
      };
      this.$refs.form && this.$refs.form.resetFields();
    }
  }
};
</script>

<style scoped>
.staff-manage {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 56px;
}

.search-area {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
}

.search-form {
  flex: 1;
}

.btn-group {
  display: flex;
  gap: 10px;
}

.table-card {
  margin-top: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.table-card >>> .el-table {
  flex: 1;
  display: block !important;
}

.danger-btn {
  color: #F56C6C;
}

.danger-btn:hover {
  color: #f78989;
}

.op-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  line-height: 1;
}

.op-row + .op-row {
  margin-top: 2px;
}

.op-btn-account,
.op-btn-withdraw-record {
  margin-left: 10px;
}

.account-popover__line {
  font-size: 13px;
  line-height: 1.8;
  color: #606266;
}

.account-popover__loading {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.withdraw-records-wrap {
  min-height: 260px;
  max-height: 560px;
  overflow: auto;
  padding-right: 6px;
}

.withdraw-records-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 48px 0;
}

.withdraw-record-card {
  border-radius: 8px;
}

.withdraw-record-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.withdraw-record-card__amt {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}

.withdraw-record-card__meta {
  margin-top: 8px;
  font-size: 12px;
  color: #606266;
}

.withdraw-record-card__sep {
  margin: 0 8px;
  color: #c0c4cc;
}

.withdraw-record-card__reason {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ebeef5;
}

.withdraw-record-card__reason-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.withdraw-record-card__reason-body {
  font-size: 13px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}

.withdraw-status {
  font-weight: 700;
  font-size: 13px;
}

.withdraw-status--approved {
  color: #67c23a;
}

.withdraw-status--rejected {
  color: #e6a23c;
}

.withdraw-status--pending {
  color: #409eff;
}

.paid-text {
  font-weight: 700;
}

.paid-text--unpaid {
  color: #f56c6c;
}

.paid-text--paid {
  color: #67c23a;
}

.select-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #ebeef5;
  margin-top: 5px;
}

.select-pagination .page-info {
  margin: 0 15px;
  font-size: 12px;
  color: #606266;
}

.select-pagination .el-button {
  padding: 5px 10px;
  font-size: 12px;
}

.password-strength {
  margin-top: -10px;
  margin-bottom: 18px;
  margin-left: 100px;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.strength-label {
  color: #909399;
  margin-right: 8px;
}

.password-strength .el-rate {
  height: 20px;
}

.password-strength .el-rate__icon {
  font-size: 18px;
}

.staff-deal-rate-header {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
}

.staff-deal-rate-info-icon {
  color: #53a8ff;
  cursor: help;
  font-size: 14px;
  outline: none;
}

.staff-deal-rate-popover-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
}
</style>
